import { Link } from 'react-router-dom';
import type { Person } from '../api/use-people.js';
import { attendeeOutcome, type BookedLesson } from '../api/use-bookings.js';
import { SignatureCodeLine } from './signature-codes.js';

/**
 * Your athletes, on the parent's Home (stage 4b, mockup *Signature in the
 * Portal*, parent row): one row per child, opening their page. A child with a
 * Performance Signature shows its codes; one without shows their next lesson,
 * or just their name. Nothing for an account with no children.
 *
 * Home reads the people itself, alongside the lessons, and hands them in: the
 * rows draw as soon as the people arrive, and a next lesson fills in when the
 * lessons do.
 */
export function YourAthletes({ people, lessons }: { people: Person[]; lessons: BookedLesson[] }) {
  const children = people.filter((person) => !person.is_account_holder);
  if (children.length === 0) return null;

  const now = Date.now();
  const nextFor = (clientId: string) =>
    lessons
      .filter((lesson) => lesson.attendees.some((a) => a.client_id === clientId && attendeeOutcome(lesson, a) === 'Booked'))
      .filter((lesson) => Date.parse(lesson.slot_start) > now)
      .sort((a, b) => Date.parse(a.slot_start) - Date.parse(b.slot_start))[0];

  return (
    <>
      <span className="section-label">Your athletes</span>
      {children.map((child) => {
        const next = child.signature ? undefined : nextFor(child.client_id);
        return (
          <Link key={child.client_id} className="hour-row" to={`/account/people/${child.client_id}?from=home`}>
            <span className="hour-row-text">
              <span className="name">
                {child.first_name} {child.last_name}
              </span>
              {child.signature ? <SignatureCodeLine codes={child.signature} /> : null}
              {next?.is_clinic ? (
                <span className="detail">
                  Next clinic: {next.product_name} · {when(next.slot_start)}
                </span>
              ) : next ? (
                <span className="detail">Next: {when(next.slot_start)}</span>
              ) : null}
            </span>
            <span className="chevron" aria-hidden="true">
              ›
            </span>
          </Link>
        );
      })}
    </>
  );
}

function when(iso: string): string {
  return new Date(iso).toLocaleString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}
